/**
 * The hf-admin-ui cookie: a mirror of the admin's saved console theme, for the first paint (spec
 * docs/superpowers/specs/2026-09-27-admin-console-themes-design.md, "The cookie is a mirror").
 *
 * The saved choice lives on the admin's account (super_admins.metadata.ui). The client writes the
 * cookie whenever GET admin/me answers or the picker saves, so the server layouts can paint the
 * choice before any script runs. `Path=/admin`: the cookie never reaches the play area, the portal
 * or the customer app.
 *
 * On the client the cookie is also the ONE place the current choice is read from: the
 * hf-admin-ui-change event means "the cookie changed — read it again". The console's controller
 * and the shared next-themes wrapper both read it, so they can never disagree.
 */
import { ADMIN_UI_CHANGE_EVENT, ADMIN_UI_COOKIE, type AdminUi } from './admin-themes'
import { parseAdminUi, serializeAdminUi } from './admin-ui'

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/** The exact `document.cookie` assignment for a choice. */
export function adminUiCookieString(ui: AdminUi): string {
  return `${ADMIN_UI_COOKIE}=${serializeAdminUi(ui)}; Path=/admin; SameSite=Lax; Max-Age=${ONE_YEAR_SECONDS}`
}

/** The cookie's raw text, or null. Reads `document.cookie` unless given a cookie header. */
export function readAdminUiCookieValue(cookieString?: string): string | null {
  const source = cookieString ?? (typeof document === 'undefined' ? '' : document.cookie)
  const prefix = `${ADMIN_UI_COOKIE}=`
  for (const part of source.split(';')) {
    const pair = part.trim()
    if (pair.startsWith(prefix)) return pair.slice(prefix.length)
  }
  return null
}

/** The admin's choice as the cookie holds it — classic / system when it is missing or not a catalogue choice. */
export function readAdminUiCookie(cookieString?: string): AdminUi {
  return parseAdminUi(readAdminUiCookieValue(cookieString))
}

export function writeAdminUiCookie(ui: AdminUi): void {
  if (typeof document === 'undefined') return
  document.cookie = adminUiCookieString(ui)
}

/**
 * Forget the mirror on sign-out (review A7, 2026-09-27): the next person to sign in at this browser
 * starts from Classic until GET admin/me answers with their own choice. Path=/admin, as it was set.
 */
export function clearAdminUiCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${ADMIN_UI_COOKIE}=; Path=/admin; SameSite=Lax; Max-Age=0`
}

/** Tell the console the cookie changed. Listeners read the cookie; the detail is for logging only. */
export function dispatchAdminUiChange(ui: AdminUi): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<AdminUi>(ADMIN_UI_CHANGE_EVENT, { detail: ui }))
}

/**
 * Mirror a choice: write the cookie (which also renews its year), and — only when it changed what
 * the cookie held — tell the console. Returns whether it changed.
 */
export function mirrorAdminUi(ui: AdminUi): boolean {
  const before = readAdminUiCookieValue()
  writeAdminUiCookie(ui)
  const changed = before !== serializeAdminUi(ui)
  if (changed) dispatchAdminUiChange(parseAdminUi(serializeAdminUi(ui)))
  return changed
}

// ── The save guard ──────────────────────────────────────────────────────────
//
// GET admin/me mirrors the saved choice into the cookie. An answer that was on its way while the
// admin saved a new choice may carry the OLD one, and must not overwrite the new. So a save moves
// an epoch when it starts and when it ends; GET admin/me mirrors only when no save was in flight
// when it asked and the epoch has not moved since.

let savesInFlight = 0
let epoch = 0

export function beginAdminUiSave(): void {
  savesInFlight += 1
  epoch += 1
}

export function endAdminUiSave(): void {
  savesInFlight = Math.max(0, savesInFlight - 1)
  epoch += 1
}

export const adminUiSaving = (): boolean => savesInFlight > 0
export const adminUiSaveEpoch = (): number => epoch
